<template>
  <modal :title="'Log – ' + changeOrderTitle" @close="$emit('close')">
    <div v-if="isLoading" class="audit-loading">Laden...</div>
    <p v-else-if="entries.length === 0" class="audit-empty">Geen log beschikbaar</p>
    <ul v-else class="audit-timeline">
      <li v-for="(entry, index) in entries" :key="index" class="audit-entry">
        <div class="entry-header">
          <span class="entry-meta">{{ formatDate(entry.performedAt) }}</span>
          <span class="entry-separator"> – </span>
          <strong>{{ actionLabel(entry.action) }}</strong>
          <span class="entry-separator"> – </span>
          <span class="entry-meta">{{ entry.performedBy || 'Niet beschikbaar' }}</span>
        </div>
        <ul v-if="entry.details && entry.details.changes" class="changes-list">
          <li v-for="(change, ci) in entry.details.changes" :key="ci">
            <span v-if="change.type === 'OPTION_ADDED'">
              <span style="color: green">&#x002B;</span> {{ change.value }}
            </span>
            <span v-else-if="change.type === 'OPTION_REMOVED'">
              <span style="color: red">&#x002D;</span> {{ change.value }}
            </span>
            <span v-else
              >{{ changeTypeLabel(change.type)
              }}<template v-if="change.value">: {{ change.value }}</template></span
            >
          </li>
        </ul>
        <div v-else-if="entry.details && entry.details.submitResult" class="submit-result">
          <!-- New shape (CST-4422): per-section breakdown -->
          <ul v-if="entry.details.submitResult.sections" class="submit-sections">
            <li v-for="(section, si) in entry.details.submitResult.sections" :key="si">
              <span v-if="section.success" class="result-success">
                ✓ {{ sectionLabel(section.section) }}
              </span>
              <template v-else>
                <span class="result-error">✗ {{ sectionLabel(section.section) }}</span>
                <ul v-if="section.errors && section.errors.length" class="result-errors">
                  <li v-for="(err, ei) in section.errors" :key="ei">
                    <span class="error-label">Foutmelding:</span> {{ err }}
                  </li>
                </ul>
              </template>
            </li>
          </ul>
          <!-- Old shape (rows logged before CST-4422): flat success/errors, no section breakdown -->
          <template v-else>
            <span v-if="entry.details.submitResult.success" class="result-success"> ✓ Gelukt </span>
            <template v-else>
              <span class="result-error">✗ Mislukt</span>
              <ul
                v-if="entry.details.submitResult.errors && entry.details.submitResult.errors.length"
                class="result-errors"
              >
                <li v-for="(err, ei) in entry.details.submitResult.errors" :key="ei">
                  <span class="error-label">Foutmelding:</span> {{ err }}
                </li>
              </ul>
            </template>
          </template>
        </div>
      </li>
    </ul>
  </modal>
</template>

<script>
import Modal from '@/components/generic/presentational/molecules/modal/Modal.vue'
import changeOrderService from '@/services/changeOrderService.js'

const ACTION_LABELS = {
  CREATE_ORDER: 'Aangemaakt',
  EDIT_ORDER: 'Bewerkt',
  TEST_SUBMIT_ORDER: 'Proeforder verstuurd',
  SUBMIT_ORDER: 'Wijzigingsorder ingediend',
  EMAIL_SENT: 'E-mail verstuurd',
  ARCHIVE_ORDER: 'Gearchiveerd',
}

const CHANGE_TYPE_LABELS = {
  OPTION_ADDED: 'Optie toegevoegd',
  OPTION_REMOVED: 'Optie verwijderd',
  CUSTOMER_CHANGED: 'Klant gewijzigd',
  DEALER_CHANGED: 'Dealer gewijzigd',
  DELIVERY_DATE_CHANGED: 'Leverdatum gewijzigd',
  COMMERCIAL_HOLD_ADDED: 'Commerciële blokkade toegevoegd',
  COMMERCIAL_HOLD_REMOVED: 'Commerciële blokkade verwijderd',
  SHOWROOM_HOLD_ADDED: 'Showroom blokkade toegevoegd',
  SHOWROOM_HOLD_REMOVED: 'Showroom blokkade verwijderd',
  RESERVATION_ADDED: 'Reservering toegevoegd',
  RESERVATION_REMOVED: 'Reservering verwijderd',
  LICENSE_PLATE_NUMBER_REQUESTED: 'Kenteken aangevraagd',
  LICENSE_PLATE_TYPE_CHANGED: 'Kentekentype gewijzigd',
}

const SECTION_LABELS = {
  CONFIGURATION: 'Configuratie',
  DEALER_AND_LOCATION: 'Dealer & Loslocatie',
  DELIVERY_DATE: 'Afleverdata',
  CUSTOMER: 'Klantgegevens',
  RESERVATION: 'Reservering',
  LICENSE_PLATE: 'Kenteken',
  DELETE_ORDER: 'Fabrieksorder verwijderen',
  GENERIC: 'Overig',
}

export default {
  components: { Modal },
  props: {
    changeOrderId: {
      type: Number,
      required: true,
    },
    changeOrderTitle: {
      type: String,
      required: true,
    },
  },
  emits: ['close'],
  data() {
    return {
      isLoading: true,
      entries: [],
    }
  },
  async mounted() {
    try {
      this.entries = await changeOrderService.getAuditTrail(this.changeOrderId)
    } catch {
      this.entries = []
    } finally {
      this.isLoading = false
    }
  },
  methods: {
    actionLabel(action) {
      return ACTION_LABELS[action] || 'Onbekend'
    },
    changeTypeLabel(type) {
      return CHANGE_TYPE_LABELS[type] || type
    },
    sectionLabel(section) {
      return SECTION_LABELS[section] || section
    },
    formatDate(dateStr) {
      if (!dateStr) return 'Niet beschikbaar'
      const [datePart, timePart] = dateStr.split('T')
      const [year, month, day] = datePart.split('-')
      const [hours, minutes] = timePart.split(':')
      return `${day}-${month}-${year} ${hours}:${minutes}`
    },
  },
}
</script>

<style lang="scss" scoped>
.audit-loading,
.audit-empty {
  padding: 1rem 0;
}

.audit-timeline {
  list-style: none;
  padding: 0;
  margin: 0;
  max-height: 60vh;
  overflow-y: auto;
}

.audit-entry {
  padding: 0.75rem 0;
  border-bottom: 1px solid #dae2e5;

  &:last-child {
    border-bottom: none;
  }
}

.entry-header {
  display: flex;
  gap: 1rem;
  align-items: baseline;
}

.entry-meta {
  color: #a3b8c0;
  font-size: 0.875rem;
}

.changes-list {
  list-style: none;
  padding: 0.25rem 0 0 1rem;
  margin: 0;
  font-size: 0.875rem;
  color: #555;
}

.submit-result {
  padding: 0.25rem 0 0 1rem;
  font-size: 0.875rem;
}

.submit-sections {
  list-style: none;
  padding: 0;
  margin: 0;
}

.result-success {
  color: green;
}

.result-error {
  color: red;
  font-weight: 600;
}

.result-errors {
  list-style: none;
  padding: 0.25rem 0 0 1rem;
  margin: 0;
  color: #555;
}

.error-label {
  font-weight: 600;
  color: #777;
  margin-right: 0.25rem;
}
</style>
